import React, { useState, useEffect, useRef } from 'react';
import { api, track } from '../App';

// Levenshtein distance for fuzzy typo matching
function lev(a, b) {
  if (!a) return b.length; if (!b) return a.length;
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i-1] === b[j-1] ? dp[i-1][j-1] : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
    }
  }
  return dp[m][n];
}
function suggest(text, vocab, max = 3) {
  const t = text.toLowerCase().trim();
  if (!t || vocab.length === 0) return [];
  const scored = vocab
    .map(w => ({ w, d: lev(t, w.toLowerCase()) }))
    .filter(x => x.d <= Math.max(2, Math.ceil(t.length / 3)))
    .sort((a, b) => a.d - b.d);
  return [...new Set(scored.map(x => x.w))].slice(0, max);
}

function matchIntent(raw) {
  const text = (raw || '').trim().toLowerCase();
  if (!text) return null;

  if (/^(help|\?|what can you do)/.test(text)) return { type: 'help' };

  const tasMatch = text.match(/tas[\s\/.]?(\d+\s?[a-z]?)/i);
  if (tasMatch) return { type: 'open_tas', no: 'TAS/' + tasMatch[1].trim().toUpperCase() };
  const gspMatch = text.match(/gsp[\s\/.]?(\d+)/i);
  if (gspMatch) return { type: 'open_gsp', no: 'GSP/' + gspMatch[1] };

  if (/critical/.test(text))   return { type: 'nav', view: 'tas-critical', label: 'critical TAS' };
  if (/delayed/.test(text))    return { type: 'nav', view: 'tas-delayed',  label: 'delayed TAS' };
  if (/^pending|pending orders/.test(text))
                                return { type: 'nav', view: 'tas-orders',  label: 'pending TAS' };

  if (/(how many|count of) (critical|overdue)/.test(text)) return { type: 'stat', metric: 'critical_count' };
  if (/(how many|count of) delayed/.test(text))           return { type: 'stat', metric: 'delayed_count' };
  if (/(value|amount) at risk|how much overdue|overdue value/.test(text))
                                                          return { type: 'stat', metric: 'value_at_risk' };
  if (/total (pending|qty|pcs)|pending qty|how much pending/.test(text))
                                                          return { type: 'stat', metric: 'total_pending_pcs' };

  // Season match (AW-27, SS-27 etc.)
  const seasonMatch = text.match(/\b(aw|ss|fw|sp)[\s-]?(\d{2})\b/i);
  if (seasonMatch) return { type: 'go_season', season: `${seasonMatch[1].toUpperCase()}-${seasonMatch[2]}` };

  if (/development|silhouette|forecast/.test(text)) return { type: 'nav', view: 'development', label: 'development' };
  if (/leaderboard|rankings|leader/.test(text))    return { type: 'tab', tab: 'leaderboard', label: 'rankings' };
  if (/my orders|orders/.test(text))                return { type: 'tab', tab: 'orders', label: 'my orders' };
  if (/dashboard|home|war room/.test(text))         return { type: 'nav', view: 'home', label: 'war room' };
  if (/^tas$|^garment$/.test(text))                 return { type: 'nav', view: 'tas',  label: 'TAS' };
  if (/^gsp$|^fabric$/.test(text))                  return { type: 'nav', view: 'gsp',  label: 'GSP' };

  if (text.length >= 2) return { type: 'search', q: raw.trim() };
  return null;
}

export default function ChatBot({ user, onOrderClick, setTab, setView }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('dj_chat')) || []; } catch { return []; }
  });
  const [busy, setBusy] = useState(false);
  const [vocab, setVocab] = useState([]); // for fuzzy matching
  const scrollRef = useRef(null);

  // Build vocab from silhouettes once for fuzzy suggestions
  useEffect(() => {
    api('/silhouettes').then(d => {
      if (!Array.isArray(d)) return;
      const v = new Set();
      d.forEach(g => {
        v.add(g.season);
        (g.silhouettes || []).forEach(s => {
          ['name', 'product', 'sub_category', 'designer', 'gender'].forEach(k => {
            const val = (s[k] || '').toString().trim();
            if (val && val !== '—') v.add(val);
          });
        });
      });
      // Plus core nav vocab
      ['critical', 'delayed', 'pending', 'development', 'rankings', 'tas', 'gsp', 'orders', 'casual', 'formal', 'performance', 'athleisure'].forEach(w => v.add(w));
      setVocab([...v]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    try { sessionStorage.setItem('dj_chat', JSON.stringify(messages.slice(-30))); } catch {}
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Always jump to last message when the panel opens
  useEffect(() => {
    if (open && scrollRef.current) {
      // Defer to next tick so layout settles
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      });
    }
  }, [open]);

  function pushUser(text) { setMessages(m => [...m, { role: 'user', text, ts: Date.now() }]); }
  function pushBot(content) { setMessages(m => [...m, { role: 'bot', ...content, ts: Date.now() }]); }
  function clearChat() {
    setMessages([]);
    try { sessionStorage.removeItem('dj_chat'); } catch {}
  }

  // Deep-link helper — hash format: dev/<season>/<subCat>/<silhouetteId?>
  // encodeURIComponent each segment so values with '/' don't break path parsing.
  function navDevelopment(season, subCat, silId) {
    const parts = ['dev'];
    if (season) parts.push(encodeURIComponent(season));
    if (subCat) parts.push(encodeURIComponent(subCat));
    if (silId != null) parts.push(encodeURIComponent(String(silId)));
    window.location.hash = parts.join('/');
    setView('development');
  }

  async function handleIntent(intent, originalText) {
    if (!intent) {
      // Fuzzy suggest
      const sug = suggest(originalText, vocab);
      if (sug.length) {
        return pushBot({
          text: `Did you mean…`,
          chips: sug.map(s => ({ label: s, onClick: () => { setInput(s); setTimeout(() => submit(s), 50); } }))
        });
      }
      return pushBot({ text: "Sorry, I didn't catch that. Type 'help' to see what I can do." });
    }

    setBusy(true);
    try {
      switch (intent.type) {
        case 'help':
          return pushBot({
            text: "Try one of these:",
            chips: ['TAS/625', 'critical', 'delayed', 'value at risk', 'how many critical', 'AW-27', 'casual', 'vaishalee', 'rankings']
              .map(s => ({ label: s, onClick: () => { setInput(s); setTimeout(() => submit(s), 50); } }))
          });

        case 'open_tas': {
          const data = await api(`/orders/search?q=${encodeURIComponent(intent.no)}&limit=5`);
          const list = Array.isArray(data) ? data.filter(o => (o.tas_no || '').toUpperCase().includes(intent.no.replace('TAS/', ''))) : [];
          if (list.length === 0) return pushBot({ text: `No order found matching ${intent.no}` });
          if (list.length === 1) {
            pushBot({ text: `Opening ${list[0].tas_no} — ${list[0].buyer || ''}` });
            onOrderClick(list[0]);
            setOpen(false);
            return;
          }
          return pushBot({
            text: `Found ${list.length} orders:`,
            cards: list.map(o => ({ title: o.tas_no, sub: o.buyer || '', onClick: () => { onOrderClick(o); setOpen(false); } }))
          });
        }

        case 'open_gsp':
          pushBot({ text: `Opening GSP list…` });
          setView('gsp-orders');
          setOpen(false);
          return;

        case 'go_season':
          pushBot({ text: `Opening ${intent.season} in Development…` });
          navDevelopment(intent.season);
          setOpen(false);
          return;

        case 'nav':
          pushBot({ text: `Opening ${intent.label}…` });
          setView(intent.view);
          setOpen(false);
          return;

        case 'tab':
          pushBot({ text: `Opening ${intent.label}…` });
          setTab(intent.tab);
          setOpen(false);
          return;

        case 'stat': {
          const dash = await api('/dashboard');
          const h = dash?.health || {};
          if (intent.metric === 'critical_count')
            return pushBot({ text: `${h.tas_critical || 0} critical TAS · ${(h.tas_critical_pcs || 0).toLocaleString('en-IN')} pcs.` });
          if (intent.metric === 'delayed_count')
            return pushBot({ text: `${h.tas_delayed || 0} delayed TAS · ${(h.tas_delayed_pcs || 0).toLocaleString('en-IN')} pcs.` });
          if (intent.metric === 'total_pending_pcs')
            return pushBot({ text: `${(h.tas_total_pcs || 0).toLocaleString('en-IN')} pcs pending in TAS · ${(h.gsp_total_mtrs || 0).toLocaleString('en-IN')} mtrs in GSP.` });
          if (intent.metric === 'value_at_risk') {
            const v = (h.total_value || 0) / 10000000;
            return pushBot({ text: `₹${v.toFixed(2)} Cr in pipeline · ${h.tas_critical || 0}+${h.tas_delayed || 0} TAS overdue.` });
          }
          return pushBot({ text: 'Stat unavailable.' });
        }

        case 'search': {
          const [orders, silData] = await Promise.all([
            api(`/orders/search?q=${encodeURIComponent(intent.q)}&limit=8`).catch(() => []),
            api('/silhouettes').catch(() => [])
          ]);
          const orderList = Array.isArray(orders) ? orders : [];
          const q = intent.q.toLowerCase();

          // Detect direct sub-category / product / designer / season hits → navigate
          let matchedSeason = null, matchedSubCat = null, matchedDesigner = null, matchedProduct = null;
          (Array.isArray(silData) ? silData : []).forEach(g => {
            if (g.season && g.season.toLowerCase() === q) matchedSeason = g.season;
            (g.silhouettes || []).forEach(s => {
              if ((s.sub_category || '').toLowerCase() === q) { matchedSubCat = s.sub_category; if (!matchedSeason) matchedSeason = g.season; }
              if ((s.designer    || '').toLowerCase() === q) matchedDesigner = s.designer;
              if ((s.product     || '').toLowerCase() === q) matchedProduct  = s.product;
            });
          });
          if (matchedSubCat) {
            pushBot({ text: `Opening ${matchedSubCat} (${matchedSeason}) in Development…` });
            navDevelopment(matchedSeason, matchedSubCat);
            setOpen(false);
            return;
          }
          if (matchedSeason && !matchedSubCat) {
            pushBot({ text: `Opening ${matchedSeason} in Development…` });
            navDevelopment(matchedSeason);
            setOpen(false);
            return;
          }

          // Otherwise show search results
          const silMatches = (Array.isArray(silData) ? silData : [])
            .flatMap(g => (g.silhouettes || []).map(s => ({ ...s, season: g.season })))
            .filter(s =>
              (s.name        || '').toLowerCase().includes(q) ||
              (s.product     || '').toLowerCase().includes(q) ||
              (s.sub_category|| '').toLowerCase().includes(q) ||
              (s.designer    || '').toLowerCase().includes(q) ||
              (s.gender      || '').toLowerCase().includes(q) ||
              (s.season      || '').toLowerCase().includes(q)
            ).slice(0, 8);

          if (orderList.length === 0 && silMatches.length === 0) {
            const sug = suggest(intent.q, vocab);
            if (sug.length) {
              return pushBot({
                text: `No results for "${intent.q}". Did you mean…`,
                chips: sug.map(s => ({ label: s, onClick: () => { setInput(s); setTimeout(() => submit(s), 50); } }))
              });
            }
            return pushBot({ text: `No matches for "${intent.q}".` });
          }

          // Single-result fast path → open directly without showing cards
          if (orderList.length === 1 && silMatches.length === 0) {
            const o = orderList[0];
            pushBot({ text: `Opening ${o.tas_no} — ${o.buyer || ''}` });
            onOrderClick(o);
            setOpen(false);
            return;
          }
          if (silMatches.length === 1 && orderList.length === 0) {
            const s = silMatches[0];
            pushBot({ text: `Opening ${s.name} (${s.season}) in Development…` });
            navDevelopment(s.season, s.sub_category, s.id);
            setOpen(false);
            return;
          }

          const cards = [];
          orderList.forEach(o => cards.push({
            title: o.tas_no,
            sub: `🧵 TAS · ${o.buyer || ''} · ${o.style_name || ''}`,
            onClick: () => { onOrderClick(o); setOpen(false); }
          }));
          silMatches.forEach(s => cards.push({
            title: s.name,
            sub: `✨ ${s.season} · ${s.product || ''} · ${s.sub_category || ''} · ${s.designer || ''}`,
            // Pass silhouette id so DevelopmentView opens this specific style detail
            onClick: () => { navDevelopment(s.season, s.sub_category, s.id); setOpen(false); }
          }));
          return pushBot({
            text: `Found ${orderList.length} order${orderList.length !== 1 ? 's' : ''} + ${silMatches.length} style${silMatches.length !== 1 ? 's' : ''} for "${intent.q}":`,
            cards
          });
        }

        default:
          return pushBot({ text: 'Sorry, no handler for that.' });
      }
    } catch (err) {
      pushBot({ text: 'Something went wrong: ' + (err.message || 'unknown error') });
    } finally {
      setBusy(false);
    }
  }

  function submit(forcedText) {
    const text = (forcedText != null ? forcedText : input).trim();
    if (!text) return;
    pushUser(text);
    setInput('');
    handleIntent(matchIntent(text), text);
  }

  return (
    <>
      <button
        onClick={() => { setOpen(o => { if (!o) track('chatbot.open'); return !o; }); }}
        title="Chat assistant"
        style={{
          position: 'fixed', right: 20, bottom: 96, zIndex: 250,
          width: 52, height: 52, borderRadius: '50%',
          background: 'var(--gold)',
          color: '#FFFFFF', border: 'none', cursor: 'pointer',
          boxShadow: 'var(--shadow)',
          fontSize: 22, fontWeight: 600, lineHeight: 1,
          transition: 'transform 0.15s, box-shadow 0.15s'
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      >{open ? '✕' : '💬'}</button>

      {open && (
        <div style={{
          position: 'fixed', right: 16, bottom: 158, zIndex: 240,
          width: 340, maxWidth: 'calc(100vw - 32px)', height: 460,
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 12, display: 'flex', flexDirection: 'column',
          boxShadow: 'var(--shadow)'
        }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--gold)', color: '#FFFFFF', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>R</div>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>Rishab Assistant</div>
              <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 1 }}>Search · Navigate · Stats</div>
            </div>
            <button onClick={clearChat} title="Clear chat"
              style={{ background: 'transparent', border: '1px solid var(--border2)', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', color: 'var(--text2)', fontSize: 10, letterSpacing: 0.5, fontWeight: 600 }}
            >CLEAR</button>
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--bg)' }}>
            {messages.length === 0 && (
              <div style={{ color: 'var(--text2)', fontSize: 12, textAlign: 'center', padding: 20, lineHeight: 1.6 }}>
                👋 Try: <strong style={{ color: 'var(--gold)' }}>"TAS/625"</strong>, <strong style={{ color: 'var(--gold)' }}>"critical"</strong>, <strong style={{ color: 'var(--gold)' }}>"casual"</strong>, <strong style={{ color: 'var(--gold)' }}>"vaishalee"</strong>, or <strong style={{ color: 'var(--gold)' }}>"AW-27"</strong>.
                <br /><br />Type <strong>help</strong> for full list.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                background: m.role === 'user' ? 'var(--gold)' : 'var(--bg2)',
                border: `1px solid ${m.role === 'user' ? 'var(--gold)' : 'var(--border)'}`,
                color: m.role === 'user' ? '#FFFFFF' : 'var(--text)',
                borderRadius: 10,
                padding: '8px 12px', fontSize: 12, lineHeight: 1.4,
                boxShadow: 'var(--shadow-sm)'
              }}>
                <div>{m.text}</div>
                {m.cards && m.cards.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {m.cards.map((c, j) => (
                      <button key={j} onClick={c.onClick}
                        style={{ background: 'rgba(212,185,120,0.1)', border: '1px solid rgba(212,185,120,0.2)', borderRadius: 6, padding: '6px 10px', textAlign: 'left', cursor: 'pointer', color: 'var(--text)', fontSize: 11 }}
                      >
                        <div style={{ fontWeight: 600, color: 'var(--gold)' }}>{c.title}</div>
                        <div style={{ fontSize: 10, opacity: 0.75 }}>{c.sub}</div>
                      </button>
                    ))}
                  </div>
                )}
                {m.chips && m.chips.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {m.chips.map((c, j) => (
                      <button key={j} onClick={c.onClick}
                        style={{ background: 'var(--gold-bg)', border: '1px solid rgba(212,185,120,0.3)', borderRadius: 12, padding: '3px 9px', cursor: 'pointer', color: 'var(--gold)', fontSize: 10, letterSpacing: 0.5 }}
                      >{c.label}</button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {busy && <div style={{ color: 'var(--text2)', fontSize: 11, fontStyle: 'italic' }}>thinking…</div>}
          </div>

          <form onSubmit={e => { e.preventDefault(); submit(); }}
            style={{ padding: 10, borderTop: '1px solid var(--border)', display: 'flex', gap: 6, background: 'var(--bg2)' }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask… e.g. 'casual' or 'TAS/625'"
              style={{ flex: 1, padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }}
            />
            <button type="submit"
              style={{ background: 'var(--gold)', color: '#FFFFFF', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            >Send</button>
          </form>
        </div>
      )}
    </>
  );
}
